import React, { FC, memo, useContext, useEffect, useMemo, useState } from 'react'

import { Select } from 'antd'
import { debounce } from 'lodash-es'
import { useFormContext } from 'react-hook-form'
import { WizardContext } from 'react-wizard-primitive'

import useEvent from 'Hooks/useEvent'
import useToggle from 'Hooks/useToggle'
import { fetchCities, SuggestedOption } from 'Utils/google'

const DELIMITER = '###'

interface Option extends SuggestedOption {
  cityName: string
}

interface Props {
  calcTransportCost: () => void
  className?: string
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement
  googlePlacesApiKey: string
  id?: string
  nameGroupPrefix: string
}

const CitySelect: FC<Props> = ({ calcTransportCost, className, googlePlacesApiKey, nameGroupPrefix, ...props }) => {
  const { activeStepIndex } = useContext(WizardContext) || {}

  const { setValue, register, watch } = useFormContext()
  const [options, setOptions] = useState([] as Option[])
  const [loading, , loadStatus, loadedStatus] = useToggle()
  const [open, toggleOpen] = useToggle()

  const name = `${nameGroupPrefix}[city]`
  const countryFieldName = `${nameGroupPrefix}[countryIsoCode]`
  const cityGooglePlaceIdFieldName = `${nameGroupPrefix}[cityGooglePlaceId]`

  const city = watch(name) as string
  const cityGooglePlaceId = watch(cityGooglePlaceIdFieldName)
  const countryIsoCode = watch(countryFieldName) as string
  const value = city && cityGooglePlaceId ? `${city}${DELIMITER}${cityGooglePlaceId}` : undefined

  const handleChange = useEvent((value: string) => {
    const [cityName, placeId] = value.split(DELIMITER)
    setValue(name, cityName)
    setValue(cityGooglePlaceIdFieldName, placeId)
    calcTransportCost()
  })

  const handleSearch = useEvent(
    debounce((query: string) => {
      const search = async () => {
        if (countryIsoCode) {
          loadStatus()
          const cities = await fetchCities(countryIsoCode, query, googlePlacesApiKey)
          loadedStatus()
          setOptions(
            cities.map((city) => ({
              ...city,
              cityName: city.value,
              value: `${city.value}${DELIMITER}${city.placeId}`,
            })),
          )
        }
      }
      void search()
    }, 500),
  )

  useEffect(() => {
    register({ name }, { required: 'City is required' })
    register({ name: cityGooglePlaceIdFieldName }, { required: "Sorry, city isn't supported" })
  }, [cityGooglePlaceIdFieldName, name, register])

  useEffect(() => {
    // Only auto-open on an incomplete field when moving between wizard
    // steps (Checkout) — outside a Wizard, activeStepIndex is undefined and
    // this ran once on mount regardless of context, popping the dropdown
    // open before the user had touched anything.
    if (activeStepIndex === undefined) return
    if (!cityGooglePlaceId) {
      toggleOpen(true)
      if (!options.length) {
        handleSearch(city)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepIndex])

  // Close the dropdown once a valid city is set — otherwise, when
  // AddressSelect autofills the city from the picked street address, this
  // dropdown could still be open on top of it (two dropdowns visible at
  // once).
  useEffect(() => {
    if (cityGooglePlaceId) toggleOpen(false)
  }, [cityGooglePlaceId, toggleOpen])

  // TODO: clear value only on user change countryIsoCode
  // useUpdateEffect(() => {
  //   setValue(name, null)
  //   setValue(cityGooglePlaceIdFieldName, null)
  // }, [countryIsoCode, name, setValue, cityGooglePlaceIdFieldName])

  const selectOptions = useMemo(() => {
    if (value && options.every((option) => option.value != value)) {
      return [{ value: value || '', cityName: city, label: city, placeId: cityGooglePlaceId }, ...options]
    } else {
      return options
    }
  }, [value, options, city, cityGooglePlaceId])

  return (
    <Select<string>
      showSearch
      className={`w-100 ${className}`}
      disabled={!countryIsoCode}
      filterOption={false}
      loading={loading}
      open={open}
      optionFilterProp="label"
      optionLabelProp="cityName"
      options={selectOptions}
      placeholder="Select your city"
      size="large"
      value={value}
      onChange={handleChange}
      onDropdownVisibleChange={toggleOpen}
      onSearch={handleSearch}
      {...props}
    />
  )
}

export default memo(CitySelect)
